import type { TFunction } from "i18next";

/**
 * Leitura do requisito de integração declarado pela skill.
 *
 * O identificador é o token como o autor escreveu, e carrega três formas:
 *
 *   `jira`                     — aquele template, e só ele
 *   `jira|azuredevops|gitlab`  — qualquer um dos citados
 *   `classification:project`   — qualquer integração daquela família
 *
 * As formas se misturam (`classification:project|gitlab`). A api resolve as
 * candidatas com essas mesmas regras; aqui a leitura serve só para a tela dizer
 * em português o que o token significa, e para escolher o filtro do painel.
 */

const CLASSIFICATION_PREFIX = "classification:";

export interface RequirementAlternative {
  kind: "template" | "classification";
  value: string;
}

export function parseRequirement(id: string): RequirementAlternative[] {
  return id
    .split("|")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) =>
      part.startsWith(CLASSIFICATION_PREFIX)
        ? { kind: "classification" as const, value: part.slice(CLASSIFICATION_PREFIX.length) }
        : { kind: "template" as const, value: part }
    );
}

/** O requisito em texto corrido: "qualquer gerenciador de projetos ou gitlab". */
export function requirementLabel(id: string, t: TFunction): string {
  const alternatives = parseRequirement(id);
  if (alternatives.length === 0) return id;

  return alternatives
    .map((alternative) =>
      alternative.kind === "classification"
        ? t("skills.integrations.anyOfClass", {
            classification: t(`skills.classifications.${alternative.value}`, {
              defaultValue: alternative.value,
            }),
          })
        : alternative.value
    )
    .join(t("skills.integrations.or"));
}

/**
 * O filtro do painel de escolha, quando o requisito se reduz a um só critério.
 *
 * Com alternativas não há filtro único que as represente — e não faz falta: a
 * restrição real vem do `allowedIds`, calculado pela api. O filtro aqui só
 * afina a busca de quem procura.
 */
export function requirementFilter(
  id: string
): { type: "classification" | "name"; value: string } | undefined {
  const alternatives = parseRequirement(id);
  if (alternatives.length !== 1) return undefined;

  const [only] = alternatives;
  return only.kind === "classification"
    ? { type: "classification", value: only.value }
    : { type: "name", value: only.value };
}
